import { css } from "@emotion/react"
import { Button, Typography, useAlert, useModal } from "matsuri-ui"
import { useCallback, useMemo } from "react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import dayjs from "dayjs"
import {
    ConfirmApproveAllDialogProps,
    ConfirmApproveAllDialog
} from "./ConfirmApprovaAllDialog"
import { usePhotoTourByOperationIds } from "@/hooks/usePhotoTourByOperationId"
import { useCheckinOnDateByOperationIds } from "@/hooks/useCheckinOnDateByOperationIds"
import { useOperations } from "@/hooks/useOperaion"
import {
    useApprovalRequestMutation,
    usePendingApprovalRequests
} from "@/hooks/useApprovalRequest"
import { theme } from "@/theme"
import { ApprovalRequestPageHeader } from "@/parts/ApprovalRequestPageHeader"
import { useAlreadyTakenDialog } from "@/parts/ApprovalRequest/useAlreadyTakenDialog"
import { showJpYen } from "@/utils/showJpYen"
import ApprovalRequestCard from "@/parts/ApprovalRequest/ApprovalRequestCard"
import { scheduleTimeTextFromOperation } from "@/model/operation"
import { displayCleaningType } from "@/model/cleaningInfo"
import { useTranslation } from "@/hooks/useTranslation"

interface ApprovalRequestItem {
    id: string
    placementName: string
    operationName: string
    unitPrice: number | undefined
    cleaningDate: dayjs.Dayjs
    schedule: string
    deadlineAt?: number | null
    hasCheckinOnDate: boolean
    meta: {
        cleaningId: string
    }
}

const useApprovalRequestItemList = (): {
    data: ApprovalRequestItem[]
    refetch: () => void
} => {
    /**
     * ApprovalRequestのPropsの配列を作るhook
     * やっていること
     * 1. PendingのApprovalRequestを取得
     * 2. そのApprovalRequestに紐づくCleaningを取得。これは物件名を取得するため
     * 3. そのCleaningがツアー付き清掃ならば、ツアーの名前を取得
     * 4. それらをまとめてApprovalRequestItemの配列を作る
     **/
    const { data: approvalRequestsOutput, refetch } =
        usePendingApprovalRequests()
    const approvalRequests = approvalRequestsOutput?.requests

    const usedCleaningIds = useMemo(
        () => approvalRequests?.map(request => request.cleaningId) ?? [],
        [approvalRequests]
    )

    const { data: cleanings } = useOperations(usedCleaningIds)

    const approvalRequestWithCleaningList = useMemo(
        () =>
            approvalRequests?.map(request => ({
                ...request,
                cleaning: cleanings?.find(
                    cleaning => cleaning.id === request.cleaningId
                )
            })) ?? [],
        [approvalRequests, cleanings]
    )

    const { token } = useAuth()
    const { t } = useTranslation()
    const { data: photoTours } = usePhotoTourByOperationIds(
        token,
        usedCleaningIds
    )
    const { data: hasCheckinMap } = useCheckinOnDateByOperationIds(
        token,
        usedCleaningIds
    )
    const cleaningIdAndOperationName = cleanings?.map(cleaning => {
        const photoTour = photoTours?.[cleaning.id]
        return {
            cleaningId: cleaning.id,
            photoTourName:
                photoTour?.title ??
                t(`清掃 (${displayCleaningType(cleaning.cleaningType)})`)
        }
    })

    return {
        data: approvalRequestWithCleaningList.map(request => {
            const placementName =
                request.cleaning?.listingName ??
                request.cleaning?.commonAreaName
            const operationName = cleaningIdAndOperationName?.find(
                cl => cl.cleaningId === request.cleaningId
            )?.photoTourName

            const cleaningDate = dayjs(request.cleaning?.cleaningDate)

            const timescheduleText = request.cleaning
                ? scheduleTimeTextFromOperation(request.cleaning)
                : ""

            return {
                id: request.id,
                placementName: placementName ?? "",
                operationName: operationName ?? t("清掃"),
                cleaningDate,
                meta: {
                    cleaningId: request.cleaningId
                },
                schedule: timescheduleText,
                deadlineAt: request.cleaning?.deadlineAt,
                hasCheckinOnDate:
                    hasCheckinMap?.[request.cleaningId]?.hasCheckinOnDate ??
                    false,
                // 清掃員単価が0円の場合はundefinedにする
                unitPrice:
                    request.unitPrice !== 0
                        ? request.unitPrice + (request.specialBonus ?? 0)
                        : undefined
            }
        }),
        refetch
    }
}

const summarizePerDate = <T extends string, O extends Record<T, dayjs.Dayjs>>(
    dateKey: T,
    items: O[]
) => {
    /**
     * 日付で配列をグルーピングする関数。
     * @param dateKey 日付のキー。itemsの要素はdateKeyを持っている必要がある
     * @param items 日付でグルーピングしたい配列
     * @returns 日付をキーとしたMap
     **/

    const dateMap = new Map<string, O[]>()
    items.forEach(item => {
        const date = item[dateKey]
        const formattedDate = date.format("YYYY-MM-DD")
        const list = dateMap.get(formattedDate) ?? []
        list.push(item)
        dateMap.set(formattedDate, list)
    })

    return dateMap
}

const ApprovalRequestListPage = () => {
    const { t } = useTranslation()
    const { data: approvalRequestPropsList, refetch } =
        useApprovalRequestItemList()
    const approvalRequestPropsListPerDate = useMemo(() => {
        const propsPerDate = summarizePerDate(
            "cleaningDate",
            approvalRequestPropsList
        )
        return Array.from(propsPerDate.entries())
            .sort()
            .map(([date, propsList]) => ({
                date,
                propsList
            }))
    }, [approvalRequestPropsList])

    const showToScheduleButton = useMemo(() => {
        return approvalRequestPropsListPerDate.length === 0
    }, [approvalRequestPropsListPerDate])

    const { requestApproveApprovalRequests } = useApprovalRequestMutation()

    const { throwAlert } = useAlert()

    const [openModal, closeModal] = useModal<ConfirmApproveAllDialogProps>(
        ConfirmApproveAllDialog
    )

    const { openAlreadyTakenDialogWhenApproving } = useAlreadyTakenDialog()

    const onApproveAllButtonClick = useCallback(async () => {
        const { error } = await requestApproveApprovalRequests({
            requestIds: approvalRequestPropsList.map(request => request.id)
        })

        if (error && error.parse?.()?.errorType === "NotOwnedByCleaner") {
            openAlreadyTakenDialogWhenApproving()
            return
        }

        refetch()
        throwAlert(error, {
            successMessage: t("承認しました"),
            errorMessage: t("承認に失敗しました")
        })
        closeModal()
    }, [
        requestApproveApprovalRequests,
        approvalRequestPropsList,
        refetch,
        throwAlert,
        closeModal,
        openAlreadyTakenDialogWhenApproving
    ])

    const disabledApproveAllButton = useMemo(
        () => approvalRequestPropsListPerDate.length === 0,
        [approvalRequestPropsListPerDate]
    )

    return (
        <div
            css={css`
                position: relative;
            `}
        >
            <ApprovalRequestPageHeader>
                <div
                    css={css`
                        display: flex;
                        padding: 16px;
                        background-color: ${
                            theme.palette.intentions.cleanerBackground.main
                        };
                    `}
                >
                    <Button
                        variant="filled"
                        color="primary"
                        css={css`
                            flex: 1;
                        `}
                        onClick={() =>
                            openModal({
                                onAccept: onApproveAllButtonClick,
                                onClose: closeModal
                            })
                        }
                        disabled={disabledApproveAllButton}
                    >
                        {t("一括で承認する")}
                    </Button>
                </div>
            </ApprovalRequestPageHeader>

            <div
                css={css`
                    display: grid;
                    grid-template-rows: auto;
                    align-items: start;
                    min-height: calc(
                        100lvh - 191px
                    ); /* 191pxはヘッダーの高さ */
                    padding-bottom: 34px;
                    background-color: ${
                        theme.palette.intentions.cleanerBackground.main
                    };
                `}
            >
                {approvalRequestPropsListPerDate.map(({ date, propsList }) => {
                    // 今日、明日、明後日の日付であるかどうかを判定する
                    const isToday =
                        dayjs().format("YYYY/MM/DD") ===
                        dayjs(date).format("YYYY/MM/DD")
                    const isTomorrow =
                        dayjs().add(1, "day").format("YYYY/MM/DD") ===
                        dayjs(date).format("YYYY/MM/DD")
                    const isDayAfterTomorrow =
                        dayjs().add(2, "day").format("YYYY/MM/DD") ===
                        dayjs(date).format("YYYY/MM/DD")
                    return (
                        <article
                            key={date.toString()}
                            css={css`
                                display: grid;
                            `}
                        >
                            <header
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    min-height: ${
                                        isToday || isTomorrow
                                            ? "32px" // 今日または明日であれば高さを高くする
                                            : "24px"
                                    };
                                    padding: 0px 16px;
                                    color: ${
                                        isToday || isTomorrow
                                            ? "#ffffff" // 今日または明日であれば白
                                            : "inherit"
                                    };
                                    background-color: ${
                                        isToday || isTomorrow
                                            ? theme.palette.intentions.secondary
                                                  .main // 今日または明日であれば赤
                                            : theme.palette.intentions
                                                  .cleanerGray.light
                                    };
                                `}
                            >
                                <Typography variant="caption" clamp={1}>
                                    {isToday
                                        ? t("今日")
                                        : isTomorrow
                                          ? t("明日")
                                          : isDayAfterTomorrow
                                            ? t("明後日")
                                            : dayjs(date).format("YYYY/MM/DD")}
                                </Typography>
                            </header>
                            <ul
                                css={css`
                                    display: grid;
                                    grid-template-columns: 1fr 1fr;
                                    gap: 24px 16px;
                                    padding: 24px 16px;
                                    list-style: none;
                                `}
                            >
                                {propsList.map(
                                    ({
                                        id,
                                        placementName,
                                        operationName,
                                        unitPrice,
                                        schedule,
                                        deadlineAt,
                                        hasCheckinOnDate
                                    }) => (
                                        <li
                                            key={id}
                                            css={css`
                                                display: contents;
                                            `}
                                        >
                                            <ApprovalRequestCard
                                                href={`/approval_requests/${id}`}
                                                listingName={placementName}
                                                tourName={operationName}
                                                schedule={schedule} //時刻が入ったらここに入れる
                                                deadlineAt={deadlineAt}
                                                hasCheckinOnDate={
                                                    hasCheckinOnDate
                                                }
                                                cleaningPrice={
                                                    unitPrice && (
                                                        <span>
                                                            {showJpYen(
                                                                unitPrice
                                                            )}
                                                        </span>
                                                    )
                                                }
                                            />
                                        </li>
                                    )
                                )}
                            </ul>
                        </article>
                    )
                })}
            </div>
            {showToScheduleButton && (
                <div
                    css={css`
                        position: sticky;
                        bottom: 0;
                        left: 0;
                        padding: 16px 16px 34px 16px;
                        background-color: white;
                    `}
                >
                    <Button
                        fullWidth
                        variant={"filled"}
                        color={"primary"}
                        as="a"
                        href="/schedule"
                    >
                        {t("清掃予定へ")}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default ApprovalRequestListPage
